import React, { useState, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { theme as brand } from '../../theme/colors';
const AUTO_ADVANCE_MS = 4000;
const SLIDE_TRANSITION = 'transform 900ms cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * Hero screenshot carousel — title above the phone, always-visible arrows.
 * Slides: `{ src, title, alt }`. Prefer title-free PNGs with transparent bg.
 */
export default function ScreenshotCarousel({ slides = [] }) {
  const [index, setIndex] = useState(0);
  const [hoverPaused, setHoverPaused] = useState(false);
  const titleRef = useRef(null);
  const titleWrapRef = useRef(null);
  const count = slides.length;

  const goTo = useCallback(
    (next) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count]
  );

  const goPrev = useCallback(() => goTo(index - 1), [goTo, index]);
  const goNext = useCallback(() => goTo(index + 1), [goTo, index]);

  useEffect(() => {
    if (count <= 1) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    const tick = () => {
      if (hoverPaused || document.hidden) return;
      setIndex((prev) => (prev + 1) % count);
    };

    const id = window.setInterval(tick, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [count, hoverPaused]);

  const activeTitle = count > 0 ? slides[index]?.title : '';

  // Shrink long captions on narrow viewports so they stay on one line.
  useLayoutEffect(() => {
    const titleEl = titleRef.current;
    const wrapEl = titleWrapRef.current;
    if (!titleEl || !wrapEl || !activeTitle) return undefined;

    const fitTitle = () => {
      titleEl.style.fontSize = '';
      const basePx = parseFloat(window.getComputedStyle(titleEl).fontSize);
      const maxWidth = wrapEl.clientWidth;
      const naturalWidth = titleEl.scrollWidth;
      if (naturalWidth > maxWidth && maxWidth > 0 && basePx > 0) {
        titleEl.style.fontSize = `${basePx * (maxWidth / naturalWidth)}px`;
      }
    };

    const runFit = () => {
      fitTitle();
      if (document.fonts?.ready) {
        document.fonts.ready.then(fitTitle);
      }
    };

    runFit();
    window.addEventListener('resize', runFit);
    return () => window.removeEventListener('resize', runFit);
  }, [activeTitle, index]);

  if (count === 0) return null;

  const canNavigate = count > 1;

  const arrowButtonSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    width: 48,
    height: 48,
    bgcolor: '#fff',
    color: brand.primary,
    border: `2px solid ${brand.primary}`,
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.1)',
    transition: 'background-color 0.15s ease, opacity 0.15s ease',
    '&:hover': {
      bgcolor: `${brand.primary}08`,
    },
    '&.Mui-disabled': {
      bgcolor: '#fff',
      color: brand.primary,
      borderColor: brand.primary,
      opacity: 0.4,
    },
  };

  return (
    <Box
      onMouseEnter={() => setHoverPaused(true)}
      onMouseLeave={() => setHoverPaused(false)}
      onFocusCapture={() => setHoverPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setHoverPaused(false);
        }
      }}
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', md: 720 },
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'visible',
      }}
    >
      {/* Caption — handwritten-style app text above image (hidden when slide
          has no title). Width matches the phone below so it's centered
          directly above it, not just within the wider carousel container. */}
      {activeTitle ? (
        <Box
          ref={titleWrapRef}
          sx={{
            position: 'relative',
            zIndex: 3,
            width: '100%',
            textAlign: 'center',
            overflow: 'visible',
            mt: 1.5,
            mb: { xs: 2.5, md: 3 },
            px: { xs: 0.5, sm: 0 },
          }}
        >
          <Typography
            ref={titleRef}
            component="span"
            key={activeTitle}
            sx={{
              m: 0,
              display: 'inline-block',
              width: 'max-content',
              maxWidth: 'none',
              whiteSpace: 'nowrap',
              textWrap: 'nowrap',
              overflowWrap: 'normal',
              wordBreak: 'normal',
              hyphens: 'none',
              fontFamily: "'Caveat', cursive",
              fontSize: { xs: '1.75rem', sm: '1.9rem', md: '1.75rem', lg: '2.1rem' },
              fontWeight: 700,
              color: '#0f172a',
              letterSpacing: 'normal',
              lineHeight: 1.15,
              transform: 'rotate(-1.5deg)',
              transformOrigin: 'center center',
              animation: 'carouselTitleFade 600ms ease-out',
              '@keyframes carouselTitleFade': {
                from: { opacity: 0, transform: 'rotate(-1.5deg) translateY(6px)' },
                to: { opacity: 1, transform: 'rotate(-1.5deg) translateY(0)' },
              },
            }}
          >
            {activeTitle}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }} />
      )}

      {/* Phone + overlaid arrows */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: { xs: 300, sm: 420, md: 360, lg: 500 },
          mx: 'auto',
        }}
      >
        <IconButton
          aria-label="Previous screenshot"
          onClick={goPrev}
          disabled={!canNavigate}
          sx={{
            ...arrowButtonSx,
            left: { xs: 4, sm: 12, md: 4, lg: 20 },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 30, color: brand.primary }} />
        </IconButton>

        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: 280, sm: 400, md: 340, lg: 480 },
            mx: 'auto',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              transform: `translateX(-${index * 100}%)`,
              transition: SLIDE_TRANSITION,
              willChange: 'transform',
            }}
          >
            {slides.map((slide, i) => (
              <Box
                key={slide.src || i}
                sx={{
                  flex: '0 0 100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box
                  component="img"
                  src={slide.src}
                  alt={slide.alt || slide.title || 'App screenshot'}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  draggable={false}
                  sx={{
                    width: '100%',
                    maxWidth: { xs: 260, sm: 380, md: 300, lg: 440 },
                    height: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                    userSelect: 'none',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        <IconButton
          aria-label="Next screenshot"
          onClick={goNext}
          disabled={!canNavigate}
          sx={{
            ...arrowButtonSx,
            right: { xs: 4, sm: 12, md: 4, lg: 20 },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 30, color: brand.primary }} />
        </IconButton>
      </Box>

      {/* Slide indicators — always reserved so layout doesn't jump when slides are added */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          mt: 2.5,
          minHeight: 8,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {slides.map((slide, i) => (
          <Box
            key={slide.src || i}
            component="button"
            type="button"
            aria-label={`Go to screenshot ${i + 1}`}
            aria-current={i === index ? 'true' : undefined}
            onClick={() => goTo(i)}
            disabled={!canNavigate}
            sx={{
              p: 0,
              border: 'none',
              cursor: canNavigate ? 'pointer' : 'default',
              width: i === index ? 20 : 8,
              height: 8,
              borderRadius: 999,
              bgcolor: i === index ? brand.primary : '#cbd5e1',
              transition: 'width 0.4s ease, background-color 0.4s ease',
              '&:focus-visible': {
                outline: `2px solid ${brand.primary}`,
                outlineOffset: 2,
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
